import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import compactTheme from './theme/compactTheme';
import ProtectedRoute from './components/Navbar/ProtectedRoute';
import Navbar from './components/Navbar/Navbar';
import Jobs from './containers/Jobs/Jobs';
import JobDetails from './containers/Jobs/JobDetails';
import ManageJobs from './containers/ManageJobs/ManageJobs';
import CreateJob from './containers/ManageJobs/CreateJob';
import EditJobDetails from './containers/ManageJobs/EditJobDetails';
import './App.css';
import { store } from './state';
import { Provider } from 'react-redux';
import JobDetailsHR from './containers/Jobs/JobDetailsHR';
import { JobProvider } from './JobContext';
import ManageAssessments from './containers/ManageAssessments/ManageAssessments';
import ApplicantProfilePage from './containers/ProfilePage/ApplicantProfilePage';
import CreateAssessment from './containers/ManageAssessments/CreateAssessment';
import AddQuestions from './containers/ManageAssessments/AddQuestions';
import AllApplicants from './containers/ManageJobs/AllApplicants';
import ViewAssessmentHR from './containers/ManageAssessments/ViewAssessmentHR';
import MyApplications from './containers/MyApplications/MyApplications';
import AssessmentListPage from './containers/ApplicantAssessment/AssessmentListPage';
import AssessmentCompletionPage from './containers/ApplicantAssessment/AssessmentCompletionPage';
import AssessmentGradingPage from './containers/Metrics/AssessmentGradingPage';
import AssessmentGradingListPage from './containers/Metrics/AssessmentGradingListPage';
import LinkAssessments from './containers/ManageJobs/LinkAssessments';
import EditAssessment from './containers/ManageAssessments/EditAssessment';
import ApplicantsList from './containers/ApplicantsList/ApplicantsList';
import Dashboard from './containers/HRDashboard/Dashboard';
import { useAuth } from './contexts/AuthContext';
import Unauthorized from './components/common/Unauthorized';
import { LoadingProvider } from './contexts/LoadingContext';
import GlobalLoading from './components/common/GlobalLoading';
import Interview from './containers/Interviews/Interview';
// Home route - needs to check role after auth
const HomeRoute: React.FC = () => {
  const { keycloak, authenticated } = useAuth();
  const is_hr = authenticated && keycloak.hasResourceRole('hr');

  return (
    <ProtectedRoute>
      {is_hr ? <Navigate to="/manage-jobs" replace /> : <MyApplications />}
    </ProtectedRoute>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={compactTheme}>
      <CssBaseline />
      <Provider store={store}>
        <LoadingProvider>
          <div className="App">
            <Router>
              <GlobalLoading />
              <Navbar />
              <div className="appContent">
                <JobProvider>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/jobs" element={<Jobs />} />
                    <Route path="/jobs/:jobId" element={<JobDetails />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* Protected routes */}
                    <Route path="/" element={<HomeRoute />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute isHR={true}>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/applications"
                      element={
                        <ProtectedRoute isHR={false}>
                          <MyApplications />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/assessments/:jobId"
                      element={
                        <ProtectedRoute isHR={false}>
                          <AssessmentListPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/job/:jobId/assessment/:assessmentId"
                      element={
                        <ProtectedRoute isHR={false}>
                          <AssessmentCompletionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute isHR={false}>
                          <ApplicantProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile-view/:applicantId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <ApplicantProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-jobs"
                      element={
                        <ProtectedRoute isHR={true}>
                          <ManageJobs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-jobs/create"
                      element={
                        <JobProvider>
                          <ProtectedRoute isHR={true}>
                            <CreateJob />
                          </ProtectedRoute>
                        </JobProvider>
                      }
                    />
                    <Route
                      path="/manage-jobs/view/:jobId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <JobDetailsHR />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-jobs/edit/:jobId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <EditJobDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-jobs/create/link-assessments/:jobId"
                      element={
                        <JobProvider>
                          <ProtectedRoute isHR={true}>
                            <LinkAssessments />
                          </ProtectedRoute>
                        </JobProvider>
                      }
                    />
                    <Route
                      path="manage-jobs/applicants/:jobId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <ApplicantsList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-jobs/applicants/:jobId/interviews/:applicantId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <Interview />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-jobs/applicants/all"
                      element={
                        <ProtectedRoute isHR={true}>
                          <AllApplicants />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-assessments/create"
                      element={
                        <ProtectedRoute isHR={true}>
                          <CreateAssessment />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-assessments/addQuestions/:assessmentId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <AddQuestions />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-assessments"
                      element={
                        <ProtectedRoute isHR={true}>
                          <ManageAssessments />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-assessments/view/:assessmentId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <ViewAssessmentHR />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/manage-assessments/edit/:assessmentId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <EditAssessment />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/grade-assessment-list/job/:jobId/applicant/:applicantId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <AssessmentGradingListPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/grade-assessment/job/:jobId/applicant/:applicantId/assessment/:assessmentId"
                      element={
                        <ProtectedRoute isHR={true}>
                          <AssessmentGradingPage />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </JobProvider>
              </div>
            </Router>
          </div>
        </LoadingProvider>
      </Provider>
    </ThemeProvider>
  );
};

export default App;
